import { ACCOMMODATION_SEARCH_MAX, type DeskGuestPage, type StayGroup, type StayGroupsResponse } from "@seagames/contracts";
import { ListToolbar, Pagination, type StatsCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type DeskFilter, checkAssignments, deskGroupsOptions } from "../../lib/api/accommodation";
import { canUseDesk } from "../../lib/accommodation/access";
import { useDefaultGamesId } from "../../lib/competition/games";
import { formatClockIn, formatDateTime, zoneAbbreviation } from "../../lib/format/datetime";
import { isYmd, todayIn } from "../../lib/travel/view";
import { toast } from "../../lib/ui/toast";
import { useRoomingGuide } from "../accommodation/status";
import { EmptyCard } from "../common/empty-card";
import { StatusGuide } from "../common/status-guide";
import { BedIcon, DoorIcon, RefreshIcon } from "../helpdesk";
import { lenient, useViewSearch } from "../accommodation-noc/use-view-search";
import { DayStepper } from "../transport-trips/trip-bits";
import { useGamesTimeZone } from "../travel-leg-form";
import { type HotelGuest, inHouseOptions } from "./api";
import { type DeskDirection, DeskGroupCard, DeskGroupSheet } from "./groups";
import { GuestRow, UndoCheck } from "./guest-row";

/**
 * A hotel's own page (HOTEL login, PLAN §4 "Hotel", §0b Q3), mounted by S4's
 * `/accommodation` index for a HOTEL session — no props. Ours, not hers (she
 * has no hotel login): her check-in desk's language, phone first.
 *
 *   Arriving   today's groups by flight with the live window at the hotel
 *              (MYT; the viewer's own clock in the tooltip), landed / delayed,
 *              late arrival, overdue; Check in a whole group at once
 *   In house   who is checked in now; Check out one by one
 *   Departing  today's groups with "check out by" (the transfer's pickup)
 * Every read is polled each minute ("Updated 2:32PM"); a delay moves a window
 * by itself because the API computes it on every read. Check in / out shows
 * only with `accommodation.desk` in the hotel's group; the API decides anyway.
 *
 * Privacy (§0b Q3): only the fields a hotel may see — name, AC number,
 * organisation, gender, category, room, stay, travel + window, photo thumb.
 * Never an ID number, date of birth, phone or document; the API sends none.
 *
 * URL (`hotelAccommodationSearchShape`, spread by S4 into the index route):
 *   view      arriving (absent) | in-house | departing
 *   date      the day (games' zone; absent = today)
 *   building  one of the hotel's buildings (absent = all of them)
 *   search, page  the In house list
 */
export const hotelAccommodationSearchShape = {
	games_id: lenient(z.string().uuid()),
	view: lenient(z.enum(["in-house", "departing"])),
	date: lenient(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
	building: lenient(z.string().uuid()),
	search: lenient(z.string().trim().min(1).max(ACCOMMODATION_SEARCH_MAX)),
	page: lenient(z.coerce.number().int().min(1)),
};
type Search = z.infer<z.ZodObject<typeof hotelAccommodationSearchShape>>;
type View = "arriving" | "in-house" | "departing";

const IN_HOUSE_PAGE_SIZE = 25;

const tally = (r: StayGroupsResponse) => ({
	people: r.groups.reduce((n, g) => n + g.total, 0),
	to_do: r.groups.reduce((n, g) => n + (g.total - g.done), 0),
});

export function HotelAccommodation() {
	const { session } = useRouteContext({ from: "/_app" });
	const { search, navigate } = useViewSearch(hotelAccommodationSearchShape);
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	const buildings = (session.group?.facilities ?? []).flatMap((f) => (f.lodging_building ? [f.lodging_building] : []));

	if (!gamesId || !timeZone)
		return (
			<div className="grid gap-[1rem]" aria-busy>
				<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
				<div className="skeleton h-[12rem] rounded-[0.625rem]" />
			</div>
		);
	return <HotelDesk gamesId={gamesId} timeZone={timeZone} buildings={buildings} canCheck={canUseDesk(session)} search={search} navigate={navigate} />;
}

function HotelDesk({
	gamesId,
	timeZone,
	buildings,
	canCheck,
	search,
	navigate,
}: {
	gamesId: string;
	timeZone: string;
	buildings: { lodging_building_id: string; name: string }[];
	canCheck: boolean;
	search: Search;
	navigate: ReturnType<typeof useViewSearch<typeof hotelAccommodationSearchShape>>["navigate"];
}) {
	const { t } = useTranslation("helpdesk");
	const { formatNumber } = useUiStrings();
	const today = todayIn(timeZone);
	const date = isYmd(search.date) ? search.date : today;
	const view: View = search.view ?? "arriving";
	const buildingIds = search.building && buildings.some((b) => b.lodging_building_id === search.building) ? search.building : undefined;
	const filter: DeskFilter = { games_id: gamesId, date, lodging_building_ids: buildingIds };
	const [open, setOpen] = useState<{ group: StayGroup; direction: DeskDirection } | null>(null);
	const guide = useRoomingGuide({ rooms: false, markers: true });

	const arrivals = useQuery(deskGroupsOptions("arrivals", filter));
	const departures = useQuery(deskGroupsOptions("departures", filter));
	const inHouse = useQuery(inHouseOptions({ games_id: gamesId, lodging_building_ids: buildingIds, q: search.search, page: search.page ?? 1, page_size: IN_HOUSE_PAGE_SIZE }));
	// The tile counts everyone in house, whatever the list's search box says: the unsearched first page carries that total.
	const inHouseAll = useQuery(inHouseOptions({ games_id: gamesId, lodging_building_ids: buildingIds, page: 1, page_size: IN_HOUSE_PAGE_SIZE }));

	const set = (patch: Partial<Search>) => void navigate({ search: (p) => ({ ...p, ...patch }), replace: true });
	const zone = zoneAbbreviation(timeZone);
	const updatedAt = Math.max(arrivals.dataUpdatedAt, departures.dataUpdatedAt, inHouse.dataUpdatedAt);
	const fetching = arrivals.isFetching || departures.isFetching || inHouse.isFetching || inHouseAll.isFetching;
	const refresh = () => void Promise.all([arrivals.refetch(), departures.refetch(), inHouse.refetch(), inHouseAll.refetch()]);

	// People and still-to-do per list: summed from its groups (one response, no second count).
	const a = arrivals.data ? tally(arrivals.data) : null;
	const d = departures.data ? tally(departures.data) : null;
	const cells: StatsCell[] = [
		{ key: "arriving", label: t("hotel.strip.arriving"), value: a ? a.people : "—", note: a ? t("hotel.strip.toCheckIn", { n: formatNumber(a.to_do) }) : undefined },
		{ key: "overdue", label: t("hotel.strip.overdue"), value: arrivals.data ? arrivals.data.overdue : "—", note: t("hotel.strip.overdueNote") },
		{ key: "in", label: t("hotel.strip.inHouse"), value: inHouseAll.data ? inHouseAll.data.total : "—" },
		{ key: "departing", label: t("hotel.strip.departing"), value: d ? d.people : "—", note: d ? t("hotel.strip.toCheckOut", { n: formatNumber(d.to_do) }) : undefined },
	];

	const tabs: { key: View; label: string; count?: number }[] = [
		{ key: "arriving", label: t("hotel.tab.arriving"), count: a?.to_do },
		{ key: "in-house", label: t("hotel.tab.inHouse") },
		{ key: "departing", label: t("hotel.tab.departing"), count: d?.to_do },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<div className="flex flex-wrap items-center justify-between gap-[0.625rem]">
				<div className="min-w-0">
					<h2 className="truncate text-[0.96875rem] font-semibold text-gms-navy-ink">{buildings.map((b) => b.name).join(", ") || t("hotel.title")}</h2>
					<p className="flex flex-wrap items-center gap-x-[0.375rem] text-[0.75rem] text-gms-ink-3">
						<span>{t("hotel.zoneNote", { zone })}</span>
						{updatedAt > 0 && (
							<>
								<span aria-hidden>·</span>
								<span title={formatDateTime(new Date(updatedAt).toISOString())}>{t("hotel.updated", { time: formatClockIn(new Date(updatedAt).toISOString(), timeZone) })}</span>
							</>
						)}
						<button type="button" onClick={refresh} disabled={fetching} aria-label={t("hotel.refresh")} title={t("hotel.refresh")} className="grid size-[1.5rem] cursor-pointer place-items-center rounded-[0.375rem] text-gms-navy hover:bg-gms-navy-soft disabled:opacity-50">
							<RefreshIcon className={`size-[0.875rem] ${fetching ? "animate-spin motion-reduce:animate-none" : ""}`} />
						</button>
					</p>
				</div>
				<StatusGuide title={guide.title} groups={guide.groups} size="sm" />
			</div>

			<DayStepper date={date} today={today} onDate={(v) => set({ date: v === today ? undefined : v })} />

			{buildings.length > 1 && (
				<ListToolbar
					chips={[
						{
							kind: "options",
							key: "building",
							label: t("hotel.buildingChips"),
							options: buildings.map((b) => ({ value: b.lodging_building_id, label: b.name })),
							value: buildingIds ? [buildingIds] : [],
							onChange: (v) => set({ building: v[0] ?? undefined, page: undefined }),
						},
					]}
				/>
			)}

			<StatsStrip label={t("hotel.strip.label")} cells={cells} className={fetching && a ? "opacity-70 transition-opacity" : "transition-opacity"} />

			{/* Her segmented Check-in / Check-out, with In house between. */}
			<div role="tablist" aria-label={t("hotel.tab.label")} className="grid grid-cols-3 gap-[0.25rem] rounded-[0.625rem] bg-gms-soft-2 p-[0.25rem] sm:inline-grid sm:w-auto sm:self-start">
				{tabs.map((tab) => (
					<button
						key={tab.key}
						type="button"
						role="tab"
						aria-selected={view === tab.key}
						onClick={() => view !== tab.key && set({ view: tab.key === "arriving" ? undefined : tab.key, page: undefined })}
						className={`inline-flex h-[2.25rem] min-w-0 cursor-pointer items-center justify-center gap-[0.375rem] rounded-[0.5rem] px-[0.75rem] text-[0.8125rem] whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 sm:min-w-[7.5rem] ${
							view === tab.key ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
						}`}
					>
						<span className="truncate">{tab.label}</span>
						{!!tab.count && <span className="rounded-full bg-gms-red px-[0.375rem] text-[0.6875rem] leading-[1.125rem] font-bold text-white tabular-nums">{formatNumber(tab.count)}</span>}
					</button>
				))}
			</div>

			{view === "in-house" ? (
				<InHouse
					query={inHouse}
					canCheck={canCheck}
					showBuilding={buildings.length > 1}
					timeZone={timeZone}
					search={search.search}
					page={search.page ?? 1}
					onSearch={(v) => set({ search: v || undefined, page: undefined })}
					onPage={(p) => set({ page: p === 1 ? undefined : p })}
				/>
			) : (
				<Groups
					query={view === "arriving" ? arrivals : departures}
					direction={view === "arriving" ? "arrivals" : "departures"}
					timeZone={timeZone}
					date={date}
					canCheck={canCheck}
					onOpen={(group, direction) => setOpen({ group, direction })}
				/>
			)}

			{open && <DeskGroupSheet group={open.group} direction={open.direction} filter={filter} timeZone={timeZone} canCheck={canCheck} onClose={() => setOpen(null)} />}
		</div>
	);
}

function Groups({
	query,
	direction,
	timeZone,
	date,
	canCheck,
	onOpen,
}: {
	query: ReturnType<typeof useQuery<StayGroupsResponse>>;
	direction: DeskDirection;
	timeZone: string;
	date: string;
	canCheck: boolean;
	onOpen: (group: StayGroup, direction: DeskDirection) => void;
}) {
	const { t } = useTranslation("helpdesk");
	if (query.isPending)
		return (
			<div className="grid gap-[0.625rem] lg:grid-cols-2" aria-busy>
				<div className="skeleton h-[11rem] rounded-[0.625rem]" />
				<div className="skeleton h-[11rem] rounded-[0.625rem]" />
			</div>
		);
	if (query.isError)
		return (
			<EmptyCard
				tone="bad"
				title={t("hotel.loadFailed")}
				actions={
					<button type="button" className="btn btn-sm btn-line" onClick={() => void query.refetch()}>
						{t("common.retry")}
					</button>
				}
			/>
		);
	const groups = query.data?.groups ?? [];
	if (groups.length === 0)
		return (
			<EmptyCard icon={<DoorIcon />} title={direction === "arrivals" ? t("hotel.emptyArrivingTitle") : t("hotel.emptyDepartingTitle")}>
				{direction === "arrivals" ? t("hotel.emptyArriving") : t("hotel.emptyDeparting")}
			</EmptyCard>
		);
	return (
		<>
			<p className="text-[0.8125rem] text-gms-ink-2">{direction === "arrivals" ? t("hotel.groupedArriving") : t("hotel.groupedDeparting")}</p>
			<ul className="grid gap-[0.625rem] lg:grid-cols-2">
				{groups.map((g) => (
					<li key={g.group_key}>
						<DeskGroupCard group={g} direction={direction} timeZone={timeZone} date={date} canCheck={canCheck} onOpen={() => onOpen(g, direction)} />
					</li>
				))}
			</ul>
			{query.data?.truncated && <p className="text-[0.75rem] text-gms-ink-3">{t("hotel.truncated")}</p>}
		</>
	);
}

function InHouse({
	query,
	canCheck,
	search,
	page,
	onSearch,
	onPage,
	showBuilding,
	timeZone,
}: {
	query: ReturnType<typeof useQuery<DeskGuestPage>>;
	canCheck: boolean;
	showBuilding: boolean;
	timeZone: string;
	search: string | undefined;
	page: number;
	onSearch: (v: string) => void;
	onPage: (p: number) => void;
}) {
	const { t } = useTranslation("helpdesk");
	const { formatNumber } = useUiStrings();
	const [busy, setBusy] = useState<string | null>(null);

	const checkOut = async (g: HotelGuest) => {
		setBusy(g.room_assignment_id);
		try {
			const res = await checkAssignments("OUT", [g.room_assignment_id]);
			const skip = res.skipped[0];
			if (res.done.length > 0) toast.success(t("hotel.checkedOutOne", { name: g.name }));
			else if (skip) toast.info(t("hotel.sheet.skipLine", { name: g.name, reason: t(`refusal.${skip.reason}`) }));
		} catch (err) {
			toast.error(err, t("hotel.checkOutFailed"));
		} finally {
			setBusy(null);
		}
	};

	const rows = query.data?.items ?? [];
	return (
		<div className="grid gap-[0.75rem]">
			<ListToolbar search={search ?? ""} onSearch={onSearch} searchPlaceholder={t("hotel.inHouseSearch")} />
			{query.isPending ? (
				<div className="grid gap-[0.5rem]" aria-busy>
					<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
					<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
				</div>
			) : query.isError ? (
				<EmptyCard
					tone="bad"
					title={t("hotel.loadFailed")}
					actions={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void query.refetch()}>
							{t("common.retry")}
						</button>
					}
				/>
			) : rows.length === 0 ? (
				<EmptyCard icon={<BedIcon />} title={search ? t("hotel.emptyInHouseFiltered") : t("hotel.emptyInHouseTitle")}>
					{search ? undefined : t("hotel.emptyInHouse")}
				</EmptyCard>
			) : (
				<>
					<ul className="grid overflow-hidden rounded-box border border-gms-line bg-base-100">
						{rows.map((g) => (
							<li key={g.room_assignment_id} className="border-t border-gms-line px-[0.875rem] py-[0.75rem] first:border-t-0">
								<GuestRow
									showBuilding={showBuilding}
									guest={g}
									end={
										canCheck ? (
											<>
												<button type="button" className="btn btn-xs btn-line" disabled={busy !== null} onClick={() => void checkOut(g)}>
													{busy === g.room_assignment_id && <span className="loading loading-spinner loading-xs" />}
													{t("hotel.checkOutOne")}
												</button>
												<UndoCheck guest={g} timeZone={timeZone} />
											</>
										) : undefined
									}
								/>
							</li>
						))}
					</ul>
					<Pagination
						page={page}
						pageSize={IN_HOUSE_PAGE_SIZE}
						total={query.data?.total ?? 0}
						onPageChange={onPage}
						labels={{ summary: ({ from, to, total }) => t("hotel.inHouseRange", { count: total, from: formatNumber(from), to: formatNumber(to), total: formatNumber(total) }) }}
					/>
				</>
			)}
		</div>
	);
}
